<template>
<div class="relative" ref="comp">
  <div class="overflow-x-auto" @scroll="resizeProductModelSelect">
    <table class="table w-full">
      <thead>
        <tr>
          <th style="width: 25%">Product</th>
          <th style="width: 15%">Description</th>
          <th style="width: 10%">Quantity</th>
          <th style="width: 10%">Value</th>
          <th style="width: 5%">Discount</th>
          <th style="width: 10%">Tax</th>
          <th style="width: 10%" class="text-right">Total</th>
          <th style="width: 10%" v-if="allowActions">Actions</th>
        </tr>
      </thead>

      <span is="table-editor-row"
        class="border-t-0"
        :class="{'break-after': i % 5 === 0, 'selected-invoice-item': item.product.id === preSelectedProductID}"
        v-for="(item, i) in items"
        :key="'invoice-items-' + i"
        :item="item"
        :index="i"
        :allow-actions="allowActions"
        :max="editingItemIndex === null ? Infinity : editingItemIndex"
        @edit="editItem"
        @remove="removeItem"
        @openItemHistory="$emit('openItemHistory', $event)"
      ></span>

      <ValidationObserver
        ref="observer"
        v-slot="{ invalid }"
        tag="tbody"
        v-if="allowActions"
      >
        <tr v-if="value">
          <td ref="editorModelSelectCell">
            <div class="absolute mx-auto z-30 flex flex-wrap justify-end" ref="editorModelSelectWrap">
              <model-select
                class="form-control border-0 p-0"
                ref="editorModelSelect"
                model="product"
                form-name="product"
                v-model="editor.data.product"
                :list.sync="productList"
                :search-on-mount="true"
                :is-taggable="true"
                :model-options="contactType === 'supplier' ? {supplierID: contactID, supplierAccountID: supplierAccount.id} : {}"
                :button-new="false"
                :show-title="false"
                :no-wrap="true"
                @select="selectedProduct"
                @search="searchProducts"
                @open="editor.selectProduct.open = true"
                @close="editor.selectProduct.open = false"
              />
              <button
                v-if="editor.data.product && editor.data.product.id"
                type="button"
                class="btn text-xs btn-orange py-1 flex items-center"
                @click="editProduct"
              >
                <i class="fas fa-edit"></i>
                <span class="ml-2">Edit</span>
              </button>
            </div>
          </td>
          <td>
            <input type="text" class="form-control" v-model="editor.data.description">
          </td>
          <td>
            <ValidationProvider
              rules="required|min_value:0.001"
              name="quantity"
              v-slot="{ errors }"
            >
              <input
                :key="'quantity-' + editingItemIndex"
                name="quantity"
                type="text"
                id="quantity"
                class="form-control"
                v-model="editor.data.quantity"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </td>
          <td>
            <ValidationProvider
              rules="decimal"
              name="value"
              v-slot="{ errors }"
            >
              <input
                :key="'value-' + editingItemIndex"
                name="value"
                type="text"
                id="value"
                class="form-control"
                v-model="editor.data.value"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </td>
          <td>
            <ValidationProvider
              rules="decimal|max_value:100"
              name="discount"
              v-slot="{ errors }"
            >
              <input
                :key="'discount-' + editingItemIndex"
                name="discount"
                type="text" id="discount"
                class="form-control"
                v-model="editor.data.discount"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </td>
          <td>
            <ValidationProvider
              rules="decimal"
              name="tax"
              v-slot="{ errors }"
            >
              <input
                :key="'tax-' + editingItemIndex"
                name="tax"
                type="text"
                id="tax"
                class="form-control"
                v-model="editor.data.tax"
              >
              <span class="text-sm text-red-500">{{ errors[0] }}</span>
            </ValidationProvider>
          </td>
          <td class="text-right">
            <p>
              {{ $formatMoney(editingItemTotals.all) }}
            </p>
            <p v-if="editingItemTotals.discount">
              <strong>Dis.</strong>
              {{ $formatMoney(editingItemTotals.discount) }}
            </p>
            <p v-if="editingItemTotals.tax">
              <strong>Tax</strong>
              {{ $formatMoney(editingItemTotals.tax) }}
            </p>
          </td>
          <td class="text-center">
            <div class="flex">
              <button type="button" class="btn btn-green text-xs py-2 px-3" @click="saveItem">
                <i class="fas fa-check"></i>
              </button>
              <button type="button" class="btn btn-red text-xs py-2 px-3 ml-2" @click="clearItemEditor">
                <i class="fas " :class="editingItemIndex !== null ? 'fa-undo' : 'fa-times'"></i>
              </button>
            </div>
          </td>
        </tr>
        <tr class="text-right text-red-500 warnings" v-if="value && itemPriceWarnings">
          <td colspan="8">
            <i class="fas fa-exclamation-triangle"></i>
            <span class="ml-2">{{ itemPriceWarnings }}</span>
          </td>
        </tr>
      </ValidationObserver>

      <span is="table-editor-row"
        v-for="(item, i) in items"
        :key="'invoice-items-continue-' + i"
        :item="item"
        :index="i"
        :allow-actions="allowActions"
        :min="editingItemIndex === null ? Infinity : editingItemIndex"
        @edit="editItem"
        @remove="removeItem"
        @openItemHistory="$emit('openItemHistory', $event)"
      ></span>

    </table>
  </div>
</div>
</template>

<script>
import axios from 'axios'
import Fuse from 'fuse.js'
const TableEditorRow = resolve => require(['@/components/TableEditorRow'], resolve)

export default {
  props: {
    documentType: {
      type: String,
      default: 'invoice',
    },
    invoiceID: Number,
    allowActions: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    value: {}, // active (boolean)
    preSelectedProductID: {},
    contactType: String,
    contactID: {},
    supplierAccount: {},
    transactionType: String,
  },
  components: {
    TableEditorRow,
  },
  data() {
    return {
      editingItemIndex: null,
      editor: {
        selectProduct: {
          open: false,
        },
        data: {
          description: '',
          quantity: 1,
          value: 0,
          discount: 0,
          tax: 0,
          product: {}
        },
      },
      productList: [],
      delays: {
        products: {
          d: null,
          t: 500,
        },
        positionProductSelect: {
          d: null,
          t: 150,
        },
      }
    }
  },
  watch: {
    'value'(v) {
      if (v) {
        this.$nextTick(this.resizeProductModelSelect)
      }
    },
    items() {
      this.items.map(v => {
        v.priceWarnings = this.comparePrices(v, v.product)
      })
    },
  },
  computed: {
    hasSearchListener(){
      return this.$listeners && this.$listeners.search
    },
    itemPriceWarnings() {
      let item = this.editor.data
      let product = this.editor.data.product

      return this.comparePrices(item, product)
    },
    editingItemTotals() {
      const total = {
        raw: this.editor.data.value * this.editor.data.quantity,
      }
      total.discount = total.raw * this.editor.data.discount / 100
      total.tax = (total.raw - total.discount) * this.editor.data.tax / 100
      total.all = total.raw - total.discount + total.tax

      return total
    },
  },
  mounted() {
    this.resizeProductModelSelect()
    window.addEventListener('resize', this.resizeProductModelSelect)

    this.getCreditNoteProducts()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeProductModelSelect)
  },
  methods: {
    getCreditNoteProducts() {
      if (this.documentType !== 'credit-note') {
        return
      }
      // get product list from invoice items
      axios.get('invoices/products/' + this.invoiceID)
        .then(response => {
          this.productList = response.data.map(p => {
            p.display_name = '#' + p.id + ' ' + p.name + ' ' + (p.code || '')
            return p
          })
        })
    },
    resizeProductModelSelect() {
      if (!this.value) {
        return
      }

      let delay = this.delays.positionProductSelect
      clearTimeout(delay.d)

      delay.d = setTimeout(() => {
        let compRect = this.$refs.comp.getBoundingClientRect()
        let rect = this.$refs.editorModelSelectCell.getBoundingClientRect()
        let wrapRect = this.$refs.editorModelSelectWrap.getBoundingClientRect()

        this.$refs.editorModelSelectWrap.style.width = (rect.width - 20)  + 'px'
        this.$refs.editorModelSelectWrap.style.top = (rect.top - compRect.top) + ((rect.height - wrapRect.height) / 2)  + 'px'
        this.$refs.editorModelSelectWrap.style.left = (rect.left - compRect.left)  + 'px'
        delay.d = null
      }, delay.d ? delay.t : 0)
    },
    editProduct() {
      this.$refs.editorModelSelect.edit()
    },
    removeItem(index) {
      this.$swal.fire({
        title: 'Delete item',
        text: 'Are you sure?',
        type: 'question',
        showCancelButton: true,
      })
        .then((result) => {
          if (result.value) {
            this.productList = []
            this.getCreditNoteProducts()
            this.items.splice(index, 1)
            this.$emit('item-deleted')
          }
        })
    },
    editItem(index) {
      this.editingItemIndex = index
      this.editor.data.description = this.items[this.editingItemIndex].description
      this.editor.data.quantity = this.items[this.editingItemIndex].quantity
      this.editor.data.value = this.items[this.editingItemIndex].value
      this.editor.data.discount = this.items[this.editingItemIndex].discount
      this.editor.data.tax = this.items[this.editingItemIndex].tax
      this.editor.data.product_id = this.items[this.editingItemIndex].product_id
      this.editor.data.product = this.items[this.editingItemIndex].product

      // set multiselect
      this.editor.data.product.display_name = '#' + this.editor.data.product.id + ' ' + this.editor.data.product.name + ' ' + (this.editor.data.product.code || '')
      this.productList = []
      this.getCreditNoteProducts()
      this.productList.push(this.editor.data.product)

      this.resizeProductModelSelect()

      this.$emit('input', true)
    },
    async saveItem () {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) {
        this.$notify({type: 'error', title: 'Form has errors'})
        return
      }
      this._saveItem()
    },
    _saveItem() {
      if (!this.editor.data.product || !Object.entries(this.editor.data.product).length) {
        this.$notify({
          type: 'error',
          title: 'Please select product',
          text: 'Product cannot be empty'
        })
        return
      }
      this.editor.data.product_id = this.editor.data.product.id

      let index = null
      if (this.editingItemIndex === null) {
        // create
        this.items.push(Object.assign({}, this.editor.data))
        index = this.items.length - 1
      } else {
        // edit
        let editData = {}
        if (typeof this.items[this.editingItemIndex] !== 'undefined') {
          editData = Object.assign(this.items[this.editingItemIndex], this.editor.data)
        } else {
          editData = this.editor.data
        }
        this.$set(this.items, this.editingItemIndex, editData)
        index = this.editingItemIndex
      }

      this.$emit('item-added', (promise) => {
        promise
          .then(() => {
            this.clearItemEditor()
          })
          .catch(() => {
            // revert
            this.items.splice(index, 1)

            this.editor.data.quantity = 0
          })
      })
    },
    clearItemEditor() {
      this.$emit('input', false)
      this.productList = []
      this.getCreditNoteProducts()
      this.editingItemIndex = null
      this.editor.data.description = ''
      this.editor.data.quantity = 1
      this.editor.data.value = 0
      this.editor.data.discount = 0
      this.editor.data.tax = 0
      this.editor.data.product = {}
    },
    selectedProduct(product) {
      this.editor.data.value = this.transactionType === 'sales'? product.sale_price : product.purchase_price
      this.editor.data.tax = this.transactionType === 'sales'? product.sale_tax: product.purchase_tax
      this.resizeProductModelSelect()
    },
    comparePrices(item, product) {
      if (!product) {
        return ''
      }

      const total = {
        raw: item.value * item.quantity,
      }
      total.discount = total.raw * item.discount / 100
      total.tax = (total.raw - total.discount) * item.tax / 100
      total.all = total.raw - total.discount + total.tax
      total.all = total.raw - total.discount + total.tax

      if (this.transactionType === 'sales') {
        let productSaleTotal = product.sale_price * item.quantity + (product.sale_price * item.quantity * product.sale_tax / 100)
        if (item.value < product.sale_price) {
          return 'Product\'s sale price is ' + this.$formatMoney(product.sale_price) + (total.all < productSaleTotal ? '. You will potentially lose ' + this.$formatMoney(productSaleTotal - total.all) : '') + '.'
        }
        if (total.all < productSaleTotal) {
          return 'Products total sale price plus tax is ' + this.$formatMoney(productSaleTotal) + '. You will potentially lose ' + this.$formatMoney(productSaleTotal - total.all) + '.'
        }
      } else if (this.transactionType === 'purchases') {
        if (item.value > product.purchase_price) {
          return 'Product\'s purchase price is ' + this.$formatMoney(product.purchase_price) + ' (' + this.$formatMoney(item.value - product.purchase_price) + ' increase).'
        }
      }

      return ''
    },
    searchProducts(search) {
      if (this.documentType === 'credit-note') {
        let fuse = new Fuse(this.productList, {
          keys: ['display_name']
        })
        fuse.search(search)
      } else if (this.documentType === 'invoice') {
        let delay = this.delays.products
        clearTimeout(delay.d)
        delay.d = setTimeout(() => {
          let supplierAccountID = this.contactType === 'supplier' ? this.supplierAccount.id : null
          axios.get('products', {params: {search, supplierAccountID}})
            .then(response => {
              this.productList = Object.assign([], response.data.data.map(p => {
                p.display_name = '#' + p.id + ' ' + p.name + ' ' + (p.code || '')
                delay.d = null
                return p
              }))
            })
        }, delay.d ? delay.t : 0)
      }
    },
  }
}
</script>
